import { motion } from "framer-motion";
import { Layers, ShieldCheck, Ban, Cpu } from "lucide-react";
import OverlayDiagram from "@/components/sections/OverlayDiagram";

const NOTS = [
    {
        icon: Ban,
        title: "Not ESG reporting",
        body: "Not a carbon accounting platform. Not consultant-produced periodic assessments. Not another compliance checkbox."
    },
    {
        icon: Layers,
        title: "An intelligence overlay",
        body: "Read-only intelligence that sits above your existing systems. No core integration, no data migration, no workflow disruption."
    },
    {
        icon: ShieldCheck,
        title: "Regulator-ready",
        body: "Supervisory-grade stress test output, aligned to Central Bank climate risk guidelines. Explainable, auditable, and confidence-weighted."
    },
    {
        icon: Cpu,
        title: "MSME-accessible",
        body: "Designed for the data-light reality of MSMEs in emerging markets. Scores built from operational signals, sector benchmarks, and India-specific emission factors."
    }
];

export default function IntelligenceOverlay() {
    return (
        <section className="relative py-24 lg:py-32 border-t border-navy/10 bg-paper-50" data-testid="intelligence-overlay-section">
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">The Category</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            An intelligence overlay — <em className="text-gold not-italic font-light">not another compliance tool.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Carbonis is not a competitor to your carbon accounting stack or your core banking system. It is the risk-intelligence layer that sits above them — one you can turn on without disrupting either.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10">
                    {NOTS.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.06 }}
                                className="bg-white p-7 lg:p-9 min-h-[280px] flex flex-col gap-5"
                                data-testid={`overlay-item-${i}`}
                            >
                                <div className="w-11 h-11 border border-navy/15 flex items-center justify-center">
                                    <Icon size={18} strokeWidth={1.4} className="text-gold" />
                                </div>
                                <div>
                                    <h3 className="font-serif text-2xl text-navy leading-snug tracking-tight mb-3">
                                        {it.title}
                                    </h3>
                                    <p className="text-navy/60 text-sm leading-relaxed">{it.body}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-10 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-10 py-6 px-6 md:px-8 border border-navy/15 bg-white">
                    <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold shrink-0">Zero-Integration Promise</span>
                    <span className="font-serif text-xl md:text-2xl text-navy leading-snug">
                        No core system integration. No data migration. No workflow disruption. Ever.
                    </span>
                </div>

                <OverlayDiagram />
            </div>
        </section>
    );
}
